import { makeAutoObservable, runInAction } from 'mobx';
import type { CoderRulesDashboardItem, GlobalCoderRule } from '../../api';
import { CodingApi, CoreFetch } from '../../api';
import { isDemoModeActive } from '../../services/auth/demo-mode.utils';

// ---- Types

export type CoderRulesDashboardEndpoint = 'provider' | 'support';

export interface CoderRule {
    ownerId?: string;
    ownerName?: string;
    ruleKey?: string;
    ruleNumber?: number;
    description?: string;
    isActive?: boolean;
    created?: string;
}

export interface LatestTransmissionStatus {
    transmissionId?: string;
    status?: string;
    message?: string;
    created?: string;
}

export interface ClaimRuleActivity {
    claimId: string;
    userId?: string;
    codingId?: string;
    codingCreated?: string;
    runDate?: string;
    latestTransmission?: LatestTransmissionStatus | null;
}

export interface CoderRuleSummary extends CoderRule {
    claims: ClaimRuleActivity[];
    failedCount: number;
    latestRunDate?: string;
    missingTransmissionCount: number;
    pendingCount: number;
    successCount: number;
    successRate: number | null;
    transmittedClaimCount: number;
}

export interface CoderRulesDashboardResponse {
    activeRuleCount?: number;
    generatedAt?: string;
    matchedClaimCount?: number;
    overallSuccessRate?: number | null;
    overallTransmittedCount?: number;
    ruleCount?: number;
    rules?: CoderRuleSummary[];
    sampleSize?: number;
}

/** Who the dashboard is shown for: the owner stamped on rules the API returns without one. */
export interface CoderRulesDashboardRequest {
    endpoint: CoderRulesDashboardEndpoint;
    userId?: string;
    userName: string;
}

/** A failed request is unknown, not "no global rules" (NS-3) — so it is its own state. */
export type GlobalRulesState =
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'loaded'; rules: GlobalCoderRule[] }
    | { status: 'error'; message: string };

/** Every read the dashboard makes. The store takes one, so it runs on the API, demo data, or a fake. */
export interface CoderRulesDashboardDataSource {
    /**
     * Rules arrive first (`onRulesLoaded`, with no claim metrics yet), then the resolved dashboard
     * once the claim and transmission rows are in.
     */
    loadDashboard(
        request: CoderRulesDashboardRequest,
        onRulesLoaded: (dashboard: CoderRulesDashboardResponse) => void,
    ): Promise<CoderRulesDashboardResponse>;
    loadGlobalRules(endpoint: CoderRulesDashboardEndpoint): Promise<GlobalCoderRule[]>;
}

// ---- Derivation: the API's rows → per-rule summaries, and the questions the screen asks of them. Pure.

export const SUCCESS_TRANSMISSION_STATUSES = new Set(['ACKNOWLEDGED', 'ADJUDICATED', 'COMPLETED', 'FORWARDED']);
export const FAILED_TRANSMISSION_STATUSES = new Set(['DENIED', 'REJECTED']);

export function getCreatedTime(value?: string) {
    if (!value) {
        return 0;
    }

    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
}

export function coerceRuleNumber(value: unknown) {
    if (typeof value === 'number' && Number.isInteger(value)) {
        return value;
    }

    if (typeof value !== 'string') {
        return null;
    }

    const trimmedValue = value.trim();

    if (!/^\d+$/.test(trimmedValue)) {
        return null;
    }

    return Number(trimmedValue);
}

export function getRuleKey(rule: CoderRuleSummary) {
    return (
        rule.ruleKey ||
        [rule.ownerId, rule.ruleNumber ?? 'unknown', rule.description]
            .filter(part => part !== undefined && part !== null && part !== '')
            .join('-')
    );
}

export function getClaimTransmissionStatus(claim: ClaimRuleActivity) {
    return (claim.latestTransmission?.status?.trim() || 'NO_STATUS').toUpperCase();
}

export function getClaimStatusOptions(claims: ClaimRuleActivity[]) {
    const statusCounts = new Map<string, number>();

    claims.forEach(claim => {
        const status = getClaimTransmissionStatus(claim);

        statusCounts.set(status, (statusCounts.get(status) || 0) + 1);
    });

    return Array.from(statusCounts.entries()).map(([status, count]) => ({ count, status }));
}

/** The rule list's text filter: matches "rule 12", the description or the key. */
export function filterRuleSummaries(ruleSummaries: CoderRuleSummary[], filter: string) {
    const normalizedFilter = filter.trim().toLowerCase();

    if (!normalizedFilter) {
        return ruleSummaries;
    }

    return ruleSummaries.filter(rule => {
        const haystack = [
            typeof rule.ruleNumber === 'number' ? `rule ${rule.ruleNumber}` : '',
            rule.description || '',
            rule.ruleKey || '',
        ]
            .join(' ')
            .toLowerCase();

        return haystack.includes(normalizedFilter);
    });
}

/** Claims matching the selected transmission statuses; none selected means all of them. */
export function filterClaimsByStatus(claims: ClaimRuleActivity[], selectedStatuses: string[]) {
    if (selectedStatuses.length === 0) {
        return claims;
    }

    const selected = new Set(selectedStatuses);

    return claims.filter(claim => selected.has(getClaimTransmissionStatus(claim)));
}

export function normalizeCoderRuleForUser(
    rule: CoderRule,
    index: number,
    userId: string | undefined,
    userName: string,
) {
    const ruleNumber = coerceRuleNumber(rule.ruleNumber);
    const normalizedRule: CoderRule = {
        ...rule,
        ownerId: rule.ownerId || userId || undefined,
        ownerName: rule.ownerName || userName,
        ruleKey:
            rule.ruleKey ||
            [userId || userName, ruleNumber === null ? 'unknown-rule' : `rule-${ruleNumber}`, index].join('-'),
        ruleNumber: ruleNumber === null ? undefined : ruleNumber,
    };

    return normalizedRule;
}

function getDashboardClaimActivity(item: CoderRulesDashboardItem): ClaimRuleActivity | null {
    const claimId = item.claimId?.trim();

    if (!claimId) {
        return null;
    }

    const latestTransmission =
        item.transmissionId ||
        item.transmissionCreateDate ||
        item.latestTransmissionStatus ||
        item.latestTransmissionStatusMessage
            ? {
                  transmissionId: item.transmissionId,
                  status: item.latestTransmissionStatus?.trim().toUpperCase() || undefined,
                  message: item.latestTransmissionStatusMessage?.trim() || undefined,
                  created: item.transmissionCreateDate,
              }
            : null;

    return {
        claimId,
        runDate: item.runDate,
        latestTransmission,
    };
}

function shouldReplaceDashboardClaim(existing: ClaimRuleActivity, nextClaim: ClaimRuleActivity) {
    const existingRunTime = getCreatedTime(existing.runDate);
    const nextRunTime = getCreatedTime(nextClaim.runDate);

    if (existingRunTime !== nextRunTime) {
        return nextRunTime > existingRunTime;
    }

    return getCreatedTime(nextClaim.latestTransmission?.created) > getCreatedTime(existing.latestTransmission?.created);
}

function getRuleSortValue(rule: CoderRuleSummary) {
    return typeof rule.ruleNumber === 'number' ? rule.ruleNumber : Number.MAX_SAFE_INTEGER;
}

function sortCoderRuleSummaries(ruleSummaries: CoderRuleSummary[]) {
    return ruleSummaries.sort((left, right) => {
        if (left.isActive !== right.isActive) {
            return left.isActive ? -1 : 1;
        }

        return getRuleSortValue(left) - getRuleSortValue(right);
    });
}

function getRuleGroupKey(ruleNumber: number | null, fallbackKey: string) {
    return ruleNumber === null ? fallbackKey : `rule-number-${ruleNumber}`;
}

function getLatestRuleRunDate(rule: CoderRuleSummary) {
    return rule.claims.reduce((latestRunDate: string | undefined, claim) => {
        if (!latestRunDate || getCreatedTime(claim.runDate) > getCreatedTime(latestRunDate)) {
            return claim.runDate;
        }

        return latestRunDate;
    }, undefined);
}

function buildEmptyCoderRuleSummary(rule: CoderRule): CoderRuleSummary {
    return {
        ...rule,
        claims: [],
        failedCount: 0,
        missingTransmissionCount: 0,
        pendingCount: 0,
        successCount: 0,
        successRate: null,
        transmittedClaimCount: 0,
    };
}

/** The first paint: every rule, no claim metrics yet. */
export function buildDashboardResponseFromRules(
    rules: CoderRule[],
    generatedTime: number,
): CoderRulesDashboardResponse {
    const ruleSummaries = sortCoderRuleSummaries(rules.map(buildEmptyCoderRuleSummary));

    return {
        activeRuleCount: ruleSummaries.filter(rule => rule.isActive).length,
        generatedAt: new Date(generatedTime).toISOString(),
        matchedClaimCount: 0,
        overallSuccessRate: null,
        overallTransmittedCount: 0,
        ruleCount: ruleSummaries.length,
        rules: ruleSummaries,
    };
}

/** The rules plus the dashboard endpoint's claim rows, grouped per rule with outcome counts. */
export function buildDashboardResponseFromItems(
    items: CoderRulesDashboardItem[],
    generatedTime: number,
    userId: string | undefined,
    userName: string,
    rules: CoderRule[] = [],
): CoderRulesDashboardResponse {
    const rulesByKey = new Map<string, CoderRuleSummary>();
    const claimsByRuleKey = new Map<string, Map<string, ClaimRuleActivity>>();

    rules.forEach((rule, index) => {
        const ruleNumber = coerceRuleNumber(rule.ruleNumber);
        const ruleGroupKey = getRuleGroupKey(ruleNumber, rule.ruleKey || `rules-endpoint-rule-${index}`);

        rulesByKey.set(ruleGroupKey, buildEmptyCoderRuleSummary(rule));
        claimsByRuleKey.set(ruleGroupKey, new Map());
    });

    items.forEach((item, index) => {
        const ruleNumber = coerceRuleNumber(item.ruleNumber);
        const description = item.ruleDescription?.trim() || 'No description';
        const ruleGroupKey = getRuleGroupKey(ruleNumber, `dashboard-rule-${index}`);

        if (!rulesByKey.has(ruleGroupKey)) {
            rulesByKey.set(ruleGroupKey, {
                ownerId: userId || undefined,
                ownerName: userName,
                ruleKey: ruleGroupKey,
                ruleNumber: ruleNumber === null ? undefined : ruleNumber,
                description,
                claims: [],
                failedCount: 0,
                missingTransmissionCount: 0,
                pendingCount: 0,
                successCount: 0,
                successRate: null,
                transmittedClaimCount: 0,
            });
            claimsByRuleKey.set(ruleGroupKey, new Map());
        }

        const rule = rulesByKey.get(ruleGroupKey);

        if (rule && rule.description === 'No description' && description !== 'No description') {
            rule.description = description;
        }

        const claim = getDashboardClaimActivity(item);

        if (!claim) {
            return;
        }

        const ruleClaims = claimsByRuleKey.get(ruleGroupKey);
        const existingClaim = ruleClaims?.get(claim.claimId);

        if (!existingClaim || shouldReplaceDashboardClaim(existingClaim, claim)) {
            ruleClaims?.set(claim.claimId, claim);
        }
    });

    const matchedClaimIds = new Set<string>();
    const ruleSummaries = Array.from(rulesByKey.entries()).map(([ruleGroupKey, rule]) => {
        const claims = Array.from(claimsByRuleKey.get(ruleGroupKey)?.values() || []).sort((left, right) => {
            const runDateDifference = getCreatedTime(right.runDate) - getCreatedTime(left.runDate);

            return runDateDifference || left.claimId.localeCompare(right.claimId);
        });
        const successCount = claims.filter(claim =>
            SUCCESS_TRANSMISSION_STATUSES.has(getClaimTransmissionStatus(claim)),
        ).length;
        const failedCount = claims.filter(claim =>
            FAILED_TRANSMISSION_STATUSES.has(getClaimTransmissionStatus(claim)),
        ).length;
        const missingTransmissionCount = claims.filter(
            claim => getClaimTransmissionStatus(claim) === 'NO_STATUS',
        ).length;
        const pendingCount = Math.max(claims.length - successCount - failedCount - missingTransmissionCount, 0);
        const transmittedClaimCount = successCount + failedCount;

        claims.forEach(claim => matchedClaimIds.add(claim.claimId));

        return {
            ...rule,
            claims,
            failedCount,
            latestRunDate: getLatestRuleRunDate({ ...rule, claims }),
            missingTransmissionCount,
            pendingCount,
            successCount,
            successRate: transmittedClaimCount === 0 ? null : (successCount / transmittedClaimCount) * 100,
            transmittedClaimCount,
        } satisfies CoderRuleSummary;
    });
    sortCoderRuleSummaries(ruleSummaries);
    const successCount = ruleSummaries.reduce((count, rule) => count + rule.successCount, 0);
    const transmittedCount = ruleSummaries.reduce((count, rule) => count + rule.transmittedClaimCount, 0);

    return {
        activeRuleCount: ruleSummaries.filter(rule => rule.isActive).length,
        generatedAt: new Date(generatedTime).toISOString(),
        matchedClaimCount: matchedClaimIds.size,
        overallSuccessRate: transmittedCount === 0 ? null : (successCount / transmittedCount) * 100,
        overallTransmittedCount: transmittedCount,
        ruleCount: ruleSummaries.length,
        rules: ruleSummaries,
    };
}

// ---- Cache (localStorage)

const CODER_RULES_DASHBOARD_CACHE_KEY = 'coder_rules_dashboard_cache';
const CODER_RULES_DASHBOARD_CACHE_VERSION = 5;

export interface CoderRulesDashboardCacheParams {
    endpoint: CoderRulesDashboardEndpoint;
    userId?: string;
    userName: string;
}

export interface CoderRulesDashboardCacheEntry {
    cacheKey: string;
    dashboard: CoderRulesDashboardResponse & { rules: CoderRuleSummary[] };
    savedAt: string;
    version: number;
}

function getStorage() {
    if (typeof window === 'undefined') {
        return null;
    }

    try {
        return window.localStorage;
    } catch {
        return null;
    }
}

export function buildCoderRulesDashboardCacheKey({ endpoint, userId, userName }: CoderRulesDashboardCacheParams) {
    return JSON.stringify({
        endpoint,
        userId: userId?.trim() || '',
        userName: userName.trim(),
    });
}

function isCacheEntry(value: unknown): value is CoderRulesDashboardCacheEntry {
    if (!value || typeof value !== 'object') {
        return false;
    }

    const entry = value as Partial<CoderRulesDashboardCacheEntry>;

    return (
        entry.version === CODER_RULES_DASHBOARD_CACHE_VERSION &&
        typeof entry.cacheKey === 'string' &&
        typeof entry.savedAt === 'string' &&
        !!entry.dashboard &&
        Array.isArray(entry.dashboard.rules)
    );
}

export function readCachedCoderRulesDashboard(cacheKey: string): CoderRulesDashboardCacheEntry | null {
    // A cache read is not a request, so the demo boundary never sees it — and a cache hit also
    // suppresses the fetch that WOULD have been blocked, making the failure silent. This cache
    // holds real claim ids, transmission ids and payer rejection messages (NS-14).
    if (isDemoModeActive()) {
        return null;
    }

    const storage = getStorage();

    if (!storage) {
        return null;
    }

    try {
        const cachedDashboard = storage.getItem(CODER_RULES_DASHBOARD_CACHE_KEY);

        if (!cachedDashboard) {
            return null;
        }

        const entry: unknown = JSON.parse(cachedDashboard);

        if (!isCacheEntry(entry) || entry.cacheKey !== cacheKey) {
            return null;
        }

        return entry;
    } catch {
        return null;
    }
}

export function saveCachedCoderRulesDashboard(cacheKey: string, dashboard: CoderRulesDashboardResponse) {
    const storage = getStorage();

    if (!storage || !dashboard.rules) {
        return;
    }

    try {
        storage.setItem(
            CODER_RULES_DASHBOARD_CACHE_KEY,
            JSON.stringify({
                cacheKey,
                dashboard: {
                    ...dashboard,
                    rules: dashboard.rules,
                },
                savedAt: new Date().toISOString(),
                version: CODER_RULES_DASHBOARD_CACHE_VERSION,
            } satisfies CoderRulesDashboardCacheEntry),
        );
    } catch {
        // localStorage may be unavailable or full. Loading from the API should still succeed.
    }
}

// ---- Data access

/** Loaded on demand — a static import ships the demo dashboard to every customer. */
const demoCoderRules = () => import('../../services/demo/coder-rules.demo');

/** The generated client rejects with the `Response` itself on a non-2xx; a message needs its body. */
async function readResponseError(response: Response) {
    const text = await response.text().catch(() => '');

    if (!text) {
        return `${response.status} ${response.statusText}`;
    }

    try {
        const payload = JSON.parse(text);
        const message = payload.error_description || payload.error || payload.message || payload.detail || text;

        return `${response.status} ${response.statusText}: ${message}`;
    } catch {
        return `${response.status} ${response.statusText}: ${text}`;
    }
}

async function unwrap<T>(request: Promise<T>): Promise<T> {
    try {
        return await request;
    } catch (caughtError) {
        throw caughtError instanceof Response ? new Error(await readResponseError(caughtError)) : caughtError;
    }
}

/**
 * Every call goes through the generated client, so the session's auth, base path and the NS-14
 * demo middleware apply. Demo mode is served from generated data instead (FE-60): refusing the
 * network is necessary but not enough to give a prospect something to look at.
 */
export const apiCoderRulesDashboardDataSource: CoderRulesDashboardDataSource = {
    async loadDashboard({ endpoint, userId, userName }: CoderRulesDashboardRequest, onRulesLoaded) {
        if (isDemoModeActive()) {
            const { buildDemoCoderRulesDashboard } = await demoCoderRules();
            const demoDashboard = buildDemoCoderRulesDashboard() as CoderRulesDashboardResponse;

            onRulesLoaded(demoDashboard);

            return demoDashboard;
        }

        const generatedTime = Date.now();
        const api = CoreFetch<CodingApi>(CodingApi);

        const rules = (
            await unwrap(endpoint === 'provider' ? api.getProviderCoderRules() : api.getSupportCoderRules())
        ).map((rule, index) => normalizeCoderRuleForUser(rule, index, userId, userName));

        onRulesLoaded(buildDashboardResponseFromRules(rules, generatedTime));

        const items = await unwrap(
            endpoint === 'provider' ? api.getProviderCoderRulesDashboard() : api.getSupportCoderRulesDashboard(),
        );

        return buildDashboardResponseFromItems(items, generatedTime, userId, userName, rules);
    },

    async loadGlobalRules(endpoint: CoderRulesDashboardEndpoint): Promise<GlobalCoderRule[]> {
        if (isDemoModeActive()) {
            const { demoGlobalCoderRules } = await demoCoderRules();

            return demoGlobalCoderRules();
        }

        const api = CoreFetch<CodingApi>(CodingApi);

        return unwrap(
            endpoint === 'provider' ? api.getProviderGlobalCoderRules({}) : api.getSupportGlobalCoderRules({}),
        );
    },
};

// ---- Store

/**
 * State for the Coders dashboard: which rules are loaded, which one is open, how its claims are
 * filtered and paged, and the global rules beside them.
 *
 * Every read goes through the injected `CoderRulesDashboardDataSource`, so the same store runs on
 * the API, on demo data (NS-14) and on a fake in tests.
 *
 * **Unknown is not none (NS-3).** `globalRules` is a state, not a list: a request that failed is
 * `error`, never an empty array, so the screen cannot read a failure as "no global rules".
 *
 * **Only the newest request may write.** Both loads carry a request id; a response for a previous
 * endpoint or an earlier click is dropped rather than allowed to overwrite the current one.
 *
 * Imports are leaf modules on purpose: the `services` barrel reaches code that needs `window`, and
 * this store is tested under node.
 */

export const CLAIMS_PAGE_SIZE = 25;
const DEFAULT_USER_NAME = 'Current user';

export interface CoderRulesDashboardStoreConfig {
    dataSource?: CoderRulesDashboardDataSource;
    endpoint: CoderRulesDashboardEndpoint;
    userId?: string;
    userName?: string;
}

/** The selected rule's claims after the status filter, and the slice of them on screen. */
export interface SelectedRuleClaims {
    ruleKey: string;
    statusOptions: Array<{ count: number; status: string }>;
    selectedStatuses: string[];
    /** Every claim matching the filter — what an export covers. */
    filtered: CoderRuleSummary['claims'];
    /** The page on screen. */
    visible: CoderRuleSummary['claims'];
    remaining: number;
}

function parseLoadedAt(value?: string) {
    if (!value) {
        return null;
    }

    const parsed = new Date(value);

    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function describeError(caughtError: unknown, fallback: string) {
    if (caughtError instanceof TypeError) {
        return 'Network request failed.';
    }

    return caughtError instanceof Error ? caughtError.message : fallback;
}

export class CoderRulesDashboardStore {
    public readonly dataSource: CoderRulesDashboardDataSource;

    public endpoint: CoderRulesDashboardEndpoint;
    public userId: string | undefined;
    public userName: string;

    public rules: CoderRuleSummary[] = [];
    public loading = false;
    public progress = '';
    public error = '';
    public lastLoadedAt: Date | null = null;

    public selectedRuleKey: string | null = null;
    public ruleFilter = '';
    public claimStatusFiltersByRuleKey: Record<string, string[]> = {};
    public visibleClaimCountByRuleKey: Record<string, number> = {};

    public globalRules: GlobalRulesState = { status: 'idle' };

    private loadRequestId = 0;
    private globalRulesRequestId = 0;

    constructor(config: CoderRulesDashboardStoreConfig) {
        this.dataSource = config.dataSource ?? apiCoderRulesDashboardDataSource;
        this.endpoint = config.endpoint;
        this.userId = config.userId?.trim() || undefined;
        this.userName = config.userName?.trim() || DEFAULT_USER_NAME;

        makeAutoObservable<CoderRulesDashboardStore, 'loadRequestId' | 'globalRulesRequestId'>(
            this,
            { dataSource: false, loadRequestId: false, globalRulesRequestId: false },
            { autoBind: true },
        );

        this.restoreFromCache();
    }

    private get request(): CoderRulesDashboardRequest {
        return { endpoint: this.endpoint, userId: this.userId, userName: this.userName };
    }

    private get cacheKey() {
        return buildCoderRulesDashboardCacheKey(this.request);
    }

    public get activeRulesCount() {
        return this.rules.filter(rule => rule.isActive).length;
    }

    public get matchedClaimCount() {
        const claimIds = new Set<string>();

        this.rules.forEach(rule => rule.claims.forEach(claim => claimIds.add(claim.claimId)));

        return claimIds.size;
    }

    public get overallTransmittedCount() {
        return this.rules.reduce((count, rule) => count + rule.transmittedClaimCount, 0);
    }

    /** Null until something has transmitted — unknown, not 0%. */
    public get overallSuccessRate() {
        if (this.overallTransmittedCount === 0) {
            return null;
        }

        const successCount = this.rules.reduce((count, rule) => count + rule.successCount, 0);

        return (successCount / this.overallTransmittedCount) * 100;
    }

    public get filteredRules() {
        return filterRuleSummaries(this.rules, this.ruleFilter);
    }

    public get selectedRule(): CoderRuleSummary | null {
        const rules = this.filteredRules;

        return rules.find(rule => getRuleKey(rule) === this.selectedRuleKey) || rules[0] || null;
    }

    public get selectedRuleClaims(): SelectedRuleClaims | null {
        const rule = this.selectedRule;

        if (!rule) {
            return null;
        }

        const ruleKey = getRuleKey(rule);
        const selectedStatuses = this.claimStatusFiltersByRuleKey[ruleKey] || [];
        const filtered = filterClaimsByStatus(rule.claims, selectedStatuses);
        const visibleCount = this.visibleClaimCountByRuleKey[ruleKey] || CLAIMS_PAGE_SIZE;

        return {
            ruleKey,
            statusOptions: getClaimStatusOptions(rule.claims),
            selectedStatuses,
            filtered,
            visible: filtered.slice(0, visibleCount),
            remaining: Math.max(filtered.length - visibleCount, 0),
        };
    }

    /**
     * Points the store at another endpoint or user, dropping what belonged to the previous one —
     * selection, filters, paging, global rules and anything still in flight. A no-op when nothing
     * changed, so a re-render with the same props does not wipe the screen.
     */
    public configure(config: Pick<CoderRulesDashboardStoreConfig, 'endpoint' | 'userId' | 'userName'>) {
        const userId = config.userId?.trim() || undefined;
        const userName = config.userName?.trim() || DEFAULT_USER_NAME;

        if (config.endpoint === this.endpoint && userId === this.userId && userName === this.userName) {
            return;
        }

        this.endpoint = config.endpoint;
        this.userId = userId;
        this.userName = userName;

        this.loadRequestId += 1;
        this.globalRulesRequestId += 1;
        this.loading = false;
        this.selectedRuleKey = null;
        this.ruleFilter = '';
        this.claimStatusFiltersByRuleKey = {};
        this.visibleClaimCountByRuleKey = {};
        this.globalRules = { status: 'idle' };
        this.error = '';
        this.restoreFromCache();
    }

    /**
     * What opening the screen does: load everything when asked to and nothing is cached; otherwise
     * show what is cached and fetch only the global rules, which are never cached.
     */
    public async start(autoLoad: boolean) {
        if (this.loading) {
            return;
        }

        if (autoLoad && this.rules.length === 0 && !this.error) {
            await this.load();

            return;
        }

        if (this.globalRules.status === 'idle') {
            await this.loadGlobalRules();
        }
    }

    public async load() {
        const requestId = ++this.loadRequestId;
        const request = this.request;
        const cacheKey = this.cacheKey;

        this.loading = true;
        this.error = '';
        this.progress = 'Loading coder rules';
        this.selectedRuleKey = null;
        this.ruleFilter = '';
        this.rules = [];
        this.visibleClaimCountByRuleKey = {};
        this.lastLoadedAt = null;

        // Alongside the dashboard, not after it — one failing must not hold up or hide the other.
        const globalRulesLoad = this.loadGlobalRules();

        try {
            const dashboard = await this.dataSource.loadDashboard(request, firstPaint => {
                if (requestId !== this.loadRequestId) {
                    return;
                }

                runInAction(() => {
                    this.rules = firstPaint.rules || [];
                    this.progress = `Loaded ${this.rules.length.toLocaleString()} coder rules. Loading dashboard claim and transmission data`;
                });
            });

            if (requestId !== this.loadRequestId) {
                return;
            }

            runInAction(() => {
                this.rules = dashboard.rules || [];
                this.lastLoadedAt = parseLoadedAt(dashboard.generatedAt) || new Date();
                this.progress = `Loaded ${(dashboard.ruleCount || dashboard.rules?.length || 0).toLocaleString()} coder rules for ${request.userName} across ${(dashboard.matchedClaimCount || 0).toLocaleString()} claims`;
            });
            saveCachedCoderRulesDashboard(cacheKey, dashboard);
        } catch (caughtError) {
            if (requestId !== this.loadRequestId) {
                return;
            }

            runInAction(() => {
                this.error = describeError(caughtError, 'Unable to load coder rule dashboard.');
                this.progress = '';
            });
        } finally {
            if (requestId === this.loadRequestId) {
                runInAction(() => {
                    this.loading = false;
                });
            }
        }

        await globalRulesLoad;
    }

    public async loadGlobalRules() {
        const requestId = ++this.globalRulesRequestId;
        const endpoint = this.endpoint;

        this.globalRules = { status: 'loading' };

        let next: GlobalRulesState;

        try {
            next = { status: 'loaded', rules: await this.dataSource.loadGlobalRules(endpoint) };
        } catch (caughtError) {
            next = { status: 'error', message: describeError(caughtError, 'Unable to load global coder rules.') };
        }

        if (requestId === this.globalRulesRequestId) {
            runInAction(() => {
                this.globalRules = next;
            });
        }
    }

    public setRuleFilter(value: string) {
        this.ruleFilter = value;
    }

    public selectRule(ruleKey: string) {
        this.selectedRuleKey = ruleKey;
    }

    public toggleClaimStatus(ruleKey: string, status: string) {
        const rule = this.rules.find(candidate => getRuleKey(candidate) === ruleKey);
        const allStatuses = getClaimStatusOptions(rule?.claims || []).map(option => option.status);
        const currentStatuses = this.claimStatusFiltersByRuleKey[ruleKey] || [];

        this.claimStatusFiltersByRuleKey = {
            ...this.claimStatusFiltersByRuleKey,
            [ruleKey]: currentStatuses.includes(status)
                ? currentStatuses.filter(item => item !== status)
                : allStatuses.filter(item => item === status || currentStatuses.includes(item)),
        };
        this.resetClaimPage(ruleKey);
    }

    public clearClaimStatuses(ruleKey: string) {
        this.claimStatusFiltersByRuleKey = { ...this.claimStatusFiltersByRuleKey, [ruleKey]: [] };
        this.resetClaimPage(ruleKey);
    }

    public showMoreClaims(ruleKey: string) {
        this.visibleClaimCountByRuleKey = {
            ...this.visibleClaimCountByRuleKey,
            [ruleKey]: (this.visibleClaimCountByRuleKey[ruleKey] || CLAIMS_PAGE_SIZE) + CLAIMS_PAGE_SIZE,
        };
    }

    private resetClaimPage(ruleKey: string) {
        this.visibleClaimCountByRuleKey = { ...this.visibleClaimCountByRuleKey, [ruleKey]: CLAIMS_PAGE_SIZE };
    }

    private restoreFromCache() {
        const cached = readCachedCoderRulesDashboard(this.cacheKey);

        this.rules = cached?.dashboard.rules || [];
        this.lastLoadedAt = parseLoadedAt(cached?.dashboard.generatedAt);
        this.progress = cached
            ? `Loaded cached coder rules from ${new Date(cached.dashboard.generatedAt || cached.savedAt).toLocaleString()}`
            : '';
    }
}
